import { useEffect, useState } from "react";
import {
  AlertCircle, Eye, EyeOff, Bookmark, Clock, Download,
  Trash2, RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * HistoryPanel — opt-in communication history per blueprint §14.
 *
 * Shows the current `history_mode` for a profile, lets the owner change
 * it, surfaces the most recent events, and exposes export/delete-all.
 *
 * Modes (mirror server-side `VALID_MODES`):
 *   off    — default, nothing logged (panel renders the explainer only)
 *   recent — 24h TTL, auto-purges
 *   saved  — only events the owner explicitly saves persist
 *   full   — everything kept until deleted/exported
 *
 * Consent: server-side, the FIRST opt-in (any non-off transition)
 * automatically writes a `communication_history_opt_in` row to the
 * consent log. We just let the user pick the mode here.
 */

const MODE_OPTIONS = [
  {
    key: "off",
    label: "Off",
    icon: EyeOff,
    sub: "Default — nothing is recorded",
  },
  {
    key: "recent",
    label: "Recent (24h)",
    icon: Clock,
    sub: "Last 24 hours auto-purged",
  },
  {
    key: "saved",
    label: "Saved phrases",
    icon: Bookmark,
    sub: "Only what you explicitly save",
  },
  {
    key: "full",
    label: "Full history",
    icon: Eye,
    sub: "Every tap until you delete",
  },
];

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function HistoryPanel({ profile, onModeChange }) {
  const [mode, setMode] = useState(profile.history_mode || "off");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Reload on profile or mode change
  const refresh = async () => {
    if (mode === "off") {
      setItems([]);
      setCount(0);
      return;
    }
    try {
      const { data } = await api.get(`/profiles/${profile.id}/history?limit=20`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (err) {
      console.warn("HistoryPanel: list failed", err?.response?.status || err?.message);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profile.id, mode]);

  const setNewMode = async (next) => {
    if (next === mode) return;
    if (mode === "off" && next !== "off") {
      const ok = window.confirm(
        "Communication history may include sensitive information — needs, feelings, pain, " +
        "location, school, family, or safety-related messages.\n\n" +
        "By turning this on, you agree to our Privacy Policy. You can delete or export at " +
        "any time. Continue?",
      );
      if (!ok) return;
    }
    setBusy(true);
    setError("");
    try {
      await api.put(`/profiles/${profile.id}/history-mode`, { history_mode: next });
      setMode(next);
      onModeChange?.(next);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const exportHistory = async () => {
    try {
      const res = await api.get(`/profiles/${profile.id}/history/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sonspeak-history-${profile.name.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteAll = async () => {
    setBusy(true);
    try {
      await api.delete(`/profiles/${profile.id}/history`);
      await refresh();
      setConfirming(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div
      className="brutal-card bg-white p-4 mb-2"
      data-testid={`history-panel-${profile.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-9 h-9 rounded-xl border-[2.5px] border-[color:var(--ink)] flex items-center justify-center text-xl"
          style={{ background: profile.color }}
          aria-hidden
        >
          {profile.avatar}
        </span>
        <div className="flex-1 min-w-0">
          <div className="display font-extrabold truncate">{profile.name}</div>
          <div className="text-xs text-[color:var(--ink-soft)]">
            {mode === "off" ? "History off — nothing recorded" : `${count} event${count === 1 ? "" : "s"} on file`}
          </div>
        </div>
      </div>

      {/* Mode picker */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        {MODE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = mode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              disabled={busy}
              onClick={() => setNewMode(opt.key)}
              className={`brutal-card p-2 text-left transition flex items-start gap-2 ${
                active
                  ? "bg-[color:var(--gold)] ring-4 ring-[color:var(--ink)]"
                  : "bg-[color:var(--paper-muted)] hover:bg-[color:var(--cream-deep)]"
              }`}
              data-testid={`history-mode-${profile.id}-${opt.key}`}
            >
              <Icon size={14} className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="display font-extrabold text-sm leading-none">{opt.label}</div>
                <div className="text-[10px] text-[color:var(--ink-soft)] mt-1 leading-tight">{opt.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 text-xs mb-2" data-testid={`history-error-${profile.id}`}>
          {error}
        </div>
      )}

      {mode !== "off" && (
        <>
          <div className="flex gap-2 mb-2 flex-wrap">
            <button type="button" onClick={refresh} className="pill-btn" data-testid={`history-refresh-${profile.id}`}>
              <RefreshCw size={13} /> Refresh
            </button>
            <button type="button" onClick={exportHistory} className="pill-btn" data-testid={`history-export-${profile.id}`}>
              <Download size={13} /> Export JSON
            </button>
            {confirming ? (
              <>
                <button type="button" onClick={deleteAll} disabled={busy} className="pill-btn danger" data-testid={`history-delete-confirm-${profile.id}`}>
                  Yes, delete all
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="pill-btn" data-testid={`history-delete-cancel-${profile.id}`}>
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="pill-btn" data-testid={`history-delete-${profile.id}`}>
                <Trash2 size={13} /> Delete all
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-xs text-[color:var(--ink-soft)] italic">
              No events yet. Tap a tile on the board to start recording.
            </div>
          ) : (
            <ul className="space-y-1 text-sm">
              {items.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]"
                  data-testid={`history-event-${e.id}`}
                >
                  <span className="text-base">{e.emoji || "💬"}</span>
                  <span className="flex-1 truncate">{e.spoken_text}</span>
                  <span className="text-[10px] text-[color:var(--ink-soft)]">{fmtTs(e.ts)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {mode === "off" && (
        <div className="flex items-start gap-2 text-xs text-[color:var(--ink-soft)] leading-relaxed">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>
            History is off — no taps are recorded. Turn on a mode above if
            you want to look back, save phrases for reuse, or share with a
            therapist who has the <code>view_history</code> permission.
          </span>
        </div>
      )}
    </div>
  );
}
